<template>
  <div>
    <section class="bg-hero-fade py-14 lg:py-20 relative">
      <div class="absolute inset-0 bg-grid-ink bg-grid-32 opacity-30 [mask-image:radial-gradient(ellipse_at_top,black_40%,transparent_70%)]"></div>

      <div class="relative container-wide max-w-6xl grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14">
        <!-- Left: context -->
        <div class="lg:col-span-2">
          <div class="eyebrow mb-3"><Icon name="mail" :size="12" /> {{ isSales ? 'Contact Sales' : 'Contact' }}</div>
          <h1 v-if="isSales" class="font-display text-4xl lg:text-5xl text-ink-900 leading-tight text-balance">
            Let's talk <span class="italic text-brand-700">Enterprise.</span>
          </h1>
          <h1 v-else class="font-display text-4xl lg:text-5xl text-ink-900 leading-tight text-balance">
            Let's <span class="italic text-brand-700">talk.</span>
          </h1>
          <p class="mt-4 text-ink-600">
            <template v-if="isSales">Custom volume, white-label, SSO, DPA — tell us what you need and we'll scope it. Average response time is under 4 business hours.</template>
            <template v-else>Questions, feedback, sales conversations — we read every message. Average response time is under 4 business hours.</template>
          </p>

          <div class="mt-8 space-y-5">
            <div v-for="c in orderedChannels" :key="c.title" class="flex gap-3">
              <span class="w-10 h-10 rounded-xl bg-white border border-ink-200 text-brand-700 shrink-0 flex items-center justify-center shadow-soft">
                <Icon :name="c.icon" :size="16" />
              </span>
              <div>
                <div class="text-sm font-semibold text-ink-900">{{ c.title }}</div>
                <div class="text-xs text-ink-500 mt-0.5">{{ c.detail }}</div>
                <a v-if="c.href" :href="c.href" class="link text-xs font-medium">{{ c.linkText }} →</a>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: form -->
        <div class="lg:col-span-3">
          <form @submit.prevent="submitContact" class="card p-6 lg:p-8">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label for="name" class="label">Your name</label>
                <input id="name" v-model="form.name" type="text" class="input" required />
              </div>
              <div>
                <label for="email" class="label">Email</label>
                <input id="email" v-model="form.email" type="email" class="input" required />
              </div>
            </div>

            <div class="mt-4">
              <label for="subject" class="label">What's this about?</label>
              <select id="subject" v-model="form.subject" class="select" required>
                <option value="">Pick a topic…</option>
                <option value="sales">Sales inquiry</option>
                <option value="support">Technical support</option>
                <option value="partnership">Partnership</option>
                <option value="press">Press / media</option>
                <option value="other">Something else</option>
              </select>
            </div>

            <div class="mt-4">
              <label for="message" class="label">Message</label>
              <textarea id="message" v-model="form.message" class="textarea" rows="6" placeholder="Tell us what you're working on." required></textarea>
            </div>

            <Transition name="fade">
              <div v-if="message" :class="messageClass" class="mt-4">
                <Icon :name="messageIsSuccess ? 'check-circle' : 'alert-triangle'" :size="16" classes="mt-0.5 shrink-0" />
                <span>{{ message }}</span>
              </div>
            </Transition>

            <div class="mt-6 flex items-center justify-between gap-3">
              <p class="text-xs text-ink-500">
                <Icon name="shield" :size="12" classes="inline -mt-0.5" /> We respect your privacy. No marketing lists.
              </p>
              <button type="submit" class="btn-primary" :disabled="loading">
                <LoadingSpinner v-if="loading" size="sm" color="#fff" track-color="rgba(255,255,255,0.3)" />
                <Icon v-else name="arrow-right" :size="14" />
                {{ loading ? 'Sending…' : 'Send message' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>

  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue';
import { useRoute } from 'vue-router';
import { usePageMeta } from '../../composables/usePageMeta.js';
import Icon from '../ui/Icon.vue';
import LoadingSpinner from '../ui/LoadingSpinner.vue';

// /contact and /contact-sales share this component. core/page_meta.py
// (server-side prerender injection) already sets a distinct title/
// description per route -- but this client-side usePageMeta() call was
// hardcoded to one title regardless of route, and since Vue's own meta
// management runs AFTER the server-injected value (and is what the
// Playwright prerender snapshot actually captures), /contact-sales was
// silently showing the generic "Contact — LeadRanks" title instead of
// its own "Contact Sales — LeadRanks" (found 2026-08-20 verifying the
// prerender fix landed). Kept in sync with core/page_meta.py's
// STATIC_PAGE_META entries for these two routes -- update both places
// together if this copy changes.
const route = useRoute();
const isSales = computed(() => route.path === '/contact-sales');
const pageTitle = computed(() => isSales.value ? 'Contact Sales — LeadRanks' : 'Contact — LeadRanks');
const pageDescription = computed(() => isSales.value
  ? 'Talk to the LeadRanks team about Agency and Enterprise plans: white-label setup, volume pricing, onboarding help, and custom API integrations.'
  : 'Get in touch with the LeadRanks team about sales, support, or partnerships. We read every message and usually reply within one business day.');

usePageMeta(() => ({
  title: pageTitle.value,
  meta: [
    { name: 'description', content: pageDescription.value },
    { property: 'og:title', content: pageTitle.value },
    { property: 'og:description', content: pageDescription.value },
    { property: 'og:image', content: 'https://leadranks.io/og-image-1200x627.png' },
    { name: 'twitter:card', content: 'summary_large_image' },
  ],
}));

// /contact-sales pre-selects the "Sales inquiry" topic -- otherwise the
// only thing that actually differed between the two routes was the <title>
// tag, not anything the visitor experiences on the page itself.
const defaultSubject = () => (isSales.value ? 'sales' : '');
const form = reactive({ name: '', email: '', subject: defaultSubject(), message: '' });
const loading = ref(false);
const message = ref('');
const messageClass = ref('');

const messageIsSuccess = computed(() => messageClass.value.includes('success'));

const submitContact = async () => {
  loading.value = true;
  message.value = '';

  try {
    const resp = await fetchWithCSRF('/api/contact', {
      method: 'POST',
      body: JSON.stringify(form),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      message.value = err.detail || "Couldn't send your message. Please try again or email us directly.";
      messageClass.value = 'alert-danger';
      return;
    }

    message.value = "Thanks — we received your message. Expect a reply within 4 business hours.";
    messageClass.value = 'alert-success';
    Object.assign(form, { name: '', email: '', subject: defaultSubject(), message: '' });
  } catch (e) {
    message.value = 'Network error. Please try again or email us directly.';
    messageClass.value = 'alert-danger';
  } finally {
    loading.value = false;
  }
};

const channels = [
  { icon: 'mail',           title: 'Email Support',   detail: 'General questions and support',  href: 'mailto:support@leadranks.io', linkText: 'support@leadranks.io' },
  { icon: 'message-circle', title: 'Sales',           detail: 'Enterprise pricing, demos, SSO',  href: 'mailto:sales@leadranks.io', linkText: 'sales@leadranks.io' },
  { icon: 'book',           title: 'Docs & knowledge',detail: 'Self-serve guides and API refs',   href: '/learn', linkText: 'Browse the library' },
];

// Sales leads with the Sales card first on /contact-sales -- same 3 items,
// just the one relevant to why someone landed on this specific URL isn't
// buried second.
const orderedChannels = computed(() => {
  if (!isSales.value) return channels;
  return [...channels].sort((a, b) => (a.title === 'Sales' ? -1 : b.title === 'Sales' ? 1 : 0));
});
</script>
